import type { AxiosProgressEvent } from "axios";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  OutreachBatchCreatePayload,
  OutreachBatchDetail,
  OutreachBatchesResponse,
  OutreachListFilters,
  OutreachOverview,
  OutreachProspectFilters,
  OutreachProspectsResponse,
  OutreachSelectionPreview,
  OutreachSelectionPreviewPayload,
  OutreachSendsResponse,
  SchoolProspectDetail,
  OutreachConfiguration,
  OutreachConfigurationPayload,
  OutreachImport,
  OutreachImportIssue,
  OutreachPaginatedResponse,
} from "~/types/admin/outreach";

class AdminOutreachModule extends HttpFactory {
  async createNativeBookingLink(payload: {
    campaign_key: string;
    meeting_type_id: number;
  }): Promise<unknown> {
    return await this.post("/api/admin/outreach/booking-link/native/", payload);
  }

  async getConfiguration(): Promise<OutreachConfiguration> {
    return await this.get(ENDPOINTS.ADMIN.OUTREACH.CONFIGURATION);
  }

  async updateConfiguration(
    payload: OutreachConfigurationPayload,
  ): Promise<OutreachConfiguration> {
    return await this.patch(ENDPOINTS.ADMIN.OUTREACH.CONFIGURATION, payload);
  }

  async createImport(
    file: File,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
  ): Promise<OutreachImport> {
    const data = new FormData();
    data.append("file", file);
    return await this.post(ENDPOINTS.ADMIN.OUTREACH.IMPORTS, data, {
      onUploadProgress,
    });
  }

  async getImports(
    filters: OutreachListFilters = {},
  ): Promise<OutreachPaginatedResponse<OutreachImport>> {
    return await this.get(ENDPOINTS.ADMIN.OUTREACH.IMPORTS, filters);
  }

  async getImport(importId: string): Promise<OutreachImport> {
    return await this.get(
      `${ENDPOINTS.ADMIN.OUTREACH.IMPORTS}${encodeURIComponent(importId)}/`,
    );
  }

  async getImportIssues(
    importId: string,
    filters: OutreachListFilters = {},
  ): Promise<OutreachPaginatedResponse<OutreachImportIssue>> {
    return await this.get(
      `${ENDPOINTS.ADMIN.OUTREACH.IMPORTS}${encodeURIComponent(importId)}/issues/`,
      filters,
    );
  }

  async getOverview(): Promise<OutreachOverview> {
    return await this.get(ENDPOINTS.ADMIN.OUTREACH.OVERVIEW);
  }

  async getProspects(
    filters: OutreachProspectFilters = {},
  ): Promise<OutreachProspectsResponse> {
    return await this.get(ENDPOINTS.ADMIN.OUTREACH.PROSPECTS, filters);
  }

  async getProspect(
    prospectId: number | string,
  ): Promise<SchoolProspectDetail> {
    const encodedProspectId = encodeURIComponent(prospectId);
    return await this.get(
      `${ENDPOINTS.ADMIN.OUTREACH.PROSPECTS}${encodedProspectId}/`,
    );
  }

  async previewSelection(
    payload: OutreachSelectionPreviewPayload,
  ): Promise<OutreachSelectionPreview> {
    return await this.post(ENDPOINTS.ADMIN.OUTREACH.SELECTION_PREVIEW, payload);
  }

  async createBatch(
    payload: OutreachBatchCreatePayload,
  ): Promise<OutreachBatchDetail> {
    return await this.post(ENDPOINTS.ADMIN.OUTREACH.BATCHES, payload);
  }

  async getBatches(
    filters: OutreachListFilters = {},
  ): Promise<OutreachBatchesResponse> {
    return await this.get(ENDPOINTS.ADMIN.OUTREACH.BATCHES, filters);
  }

  async getBatch(batchId: number | string): Promise<OutreachBatchDetail> {
    const encodedBatchId = encodeURIComponent(batchId);
    return await this.get(
      `${ENDPOINTS.ADMIN.OUTREACH.BATCHES}${encodedBatchId}/`,
    );
  }

  async getSends(
    filters: OutreachListFilters = {},
  ): Promise<OutreachSendsResponse> {
    return await this.get(ENDPOINTS.ADMIN.OUTREACH.SENDS, filters);
  }
}

export default AdminOutreachModule;
